// Keep in sync with sales-chat-experience-frontend/src/hooks/useUnreadCoachingCount.ts
// TODO: Use a backend query for unread count instead of a client Firestore listener.
import { useEffect, useMemo, useState } from 'react';
import { collection, orderBy, query, where } from 'firebase/firestore';
// @ts-ignore
import { useCollection } from 'react-firebase-hooks/firestore';

import { countUnreadCoachingSessions } from 'lib/coaching-unread';
import { UserRole, UserRoleType } from 'lib/db-interfaces';
import { firebaseDb } from 'lib/firebase';
import { useFirebaseUser } from 'providers/Auth';

export function useUnreadCoachingCount(
  repContactId: string | null | undefined,
  repRole: UserRoleType | null | undefined,
) {
  const { firebaseUser } = useFirebaseUser();
  const [userClaims, setUserClaims] = useState<Record<string, unknown> | null>(
    null,
  );
  const [claimsLoading, setClaimsLoading] = useState(false);

  useEffect(() => {
    if (!firebaseUser) {
      setUserClaims(null);
      setClaimsLoading(false);
      return;
    }

    let cancelled = false;
    setClaimsLoading(true);

    firebaseUser
      .getIdTokenResult()
      .then((idTokenResult) => {
        if (!cancelled) {
          setUserClaims(idTokenResult.claims as Record<string, unknown>);
          setClaimsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUserClaims(null);
          setClaimsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  const jwtUserType =
    typeof userClaims?.userType === 'string' ? userClaims.userType : undefined;
  const jwtSunrunContactId =
    typeof userClaims?.sunrunContactId === 'string'
      ? userClaims.sunrunContactId
      : undefined;
  const coachingContactId = jwtSunrunContactId ?? repContactId ?? null;

  const effectiveIsCxPro =
    jwtUserType === 'CX Pro' || (!jwtUserType && repRole === UserRole.CX_PRO);
  const effectiveRoleIsKnown =
    effectiveIsCxPro ||
    jwtUserType === 'Sales Rep' ||
    (!jwtUserType && repRole === UserRole.SALES_REP);

  const sessionsQuery = useMemo(() => {
    if (
      !repContactId ||
      !firebaseUser?.uid ||
      claimsLoading ||
      !userClaims ||
      !effectiveRoleIsKnown ||
      effectiveIsCxPro
    ) {
      return null;
    }

    if (!coachingContactId) {
      return null;
    }

    return query(
      collection(firebaseDb, 'coaching'),
      where('salesRepContactId', '==', coachingContactId),
      orderBy('createdAt', 'desc'),
    );
  }, [
    claimsLoading,
    coachingContactId,
    effectiveIsCxPro,
    effectiveRoleIsKnown,
    firebaseUser?.uid,
    repContactId,
    userClaims,
  ]);

  const [snapshot, firestoreLoading] = useCollection(sessionsQuery, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });

  const coachingUnreadCount = useMemo(() => {
    if (!repContactId || effectiveIsCxPro) {
      return 0;
    }

    const viewerContactId = coachingContactId ?? repContactId;
    if (!viewerContactId || !snapshot?.docs) {
      return 0;
    }

    return countUnreadCoachingSessions(snapshot.docs, viewerContactId);
  }, [coachingContactId, effectiveIsCxPro, repContactId, snapshot]);

  return {
    coachingUnreadCount,
    loading:
      Boolean(repContactId) &&
      (claimsLoading ||
        !userClaims ||
        !effectiveRoleIsKnown ||
        firestoreLoading),
  };
}
